import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Building, AlertCircle } from 'lucide-react';
import { venueClaimService, locationService, type VenueProfile, type VenueLocation } from '@/services/venueClaimService';
import { toast } from 'sonner';

interface EnhancedVenueSelectorProps {
  onVenueSelect: (venueData: {
    venue_id?: string;
    venue_name: string;
    venue_address: string;
    is_claimed?: boolean;
    venue_profile_id?: string;
  }) => void;
  className?: string;
}

export const EnhancedVenueSelector: React.FC<EnhancedVenueSelectorProps> = ({
  onVenueSelect,
  className = ''
}) => {
  const [venueProfiles, setVenueProfiles] = useState<VenueProfile[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Manual entry form state
  const [manualVenueData, setManualVenueData] = useState<VenueLocation>({
    venue_name: '',
    country: '',
    state_province: '',
    city: '',
    zip_code: '',
    address_line: ''
  });

  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  useEffect(() => {
    loadVenueProfiles();
  }, []);

  useEffect(() => {
    if (manualVenueData.country) {
      setAvailableStates(locationService.getStatesProvinces(manualVenueData.country));
      setManualVenueData(prev => ({ ...prev, state_province: '', city: '' }));
    }
  }, [manualVenueData.country]);

  useEffect(() => {
    if (manualVenueData.state_province) {
      setAvailableCities(locationService.getCities(manualVenueData.state_province));
      setManualVenueData(prev => ({ ...prev, city: '' }));
    }
  }, [manualVenueData.state_province]);

  const loadVenueProfiles = async () => {
    try {
      const profiles = await venueClaimService.getAllAvailableVenues();
      setVenueProfiles(profiles);
    } catch (error) {
      console.error('Error loading venue profiles:', error);
      toast.error('Failed to load venue profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleVenueSelection = (value: string) => {
    setSelectedVenue(value);
    
    if (value === 'no-account') {
      setShowManualEntry(true);
      return;
    }
    
    setShowManualEntry(false);
    
    const venue = venueProfiles.find(v => v.id === value);
    if (venue) {
      const fullAddress = [
        venue.address_line,
        venue.city,
        venue.state_province,
        venue.zip_code,
        venue.country
      ].filter(Boolean).join(', ');
      
      onVenueSelect({
        venue_id: venue.id,
        venue_name: venue.venue_name,
        venue_address: fullAddress,
        is_claimed: venue.is_claimed,
        venue_profile_id: venue.id
      });
    }
  };

  const handleManualVenueSubmit = async () => {
    if (!manualVenueData.venue_name || !manualVenueData.country || 
        !manualVenueData.state_province || !manualVenueData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Check if venue already exists
      const existingVenue = await venueClaimService.checkVenueExists(manualVenueData);
      
      if (existingVenue) {
        // Venue exists, use it
        const fullAddress = [
          existingVenue.address_line,
          existingVenue.city,
          existingVenue.state_province,
          existingVenue.zip_code,
          existingVenue.country
        ].filter(Boolean).join(', ');
        
        onVenueSelect({
          venue_id: existingVenue.id,
          venue_name: existingVenue.venue_name,
          venue_address: fullAddress,
          is_claimed: existingVenue.is_claimed,
          venue_profile_id: existingVenue.id
        });
        
        toast.success('Venue found in our database');
      } else {
        // Create new unclaimed venue profile and claim request
        await venueClaimService.createVenueProfile(manualVenueData);
        await venueClaimService.createClaimRequest(manualVenueData);
        
        const fullAddress = [
          manualVenueData.address_line,
          manualVenueData.city,
          manualVenueData.state_province,
          manualVenueData.zip_code,
          manualVenueData.country
        ].filter(Boolean).join(', ');
        
        onVenueSelect({
          venue_id: undefined,
          venue_name: manualVenueData.venue_name,
          venue_address: fullAddress,
          is_claimed: false
        });
        
        toast.success('Venue information submitted. Admin has been notified for account creation.');
      }
    } catch (error) {
      console.error('Error submitting venue:', error);
      toast.error('Failed to submit venue information');
    }
  };

  const updateManualVenueData = (field: keyof VenueLocation, value: string) => {
    setManualVenueData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5" />
          Venue Selection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="venue-select">Select Venue *</Label>
          <Select value={selectedVenue} onValueChange={handleVenueSelection}>
            <SelectTrigger>
              <SelectValue placeholder={loading ? "Loading venues..." : "Choose a venue"} />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border text-popover-foreground max-h-60 overflow-y-auto">
              {loading ? (
                <SelectItem value="loading" disabled>Loading venues...</SelectItem>
              ) : (
                <>
                  {venueProfiles.map((venue) => (
                    <SelectItem 
                      key={venue.id} 
                      value={venue.id}
                      className="hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{venue.venue_name}</span>
                        <span className="text-sm text-muted-foreground">
                          {venue.city}, {venue.state_province}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem 
                    value="no-account"
                    className="hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      No Account - Enter Manually
                    </div>
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {showManualEntry && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <MapPin className="h-4 w-4" />
              <span>Enter venue details manually</span>
            </div>

            <div>
              <Label htmlFor="manual-venue-name">Venue Name *</Label>
              <Input
                id="manual-venue-name"
                value={manualVenueData.venue_name}
                onChange={(e) => updateManualVenueData('venue_name', e.target.value)}
                placeholder="e.g., Central Sports Complex"
              />
            </div>

            <div>
              <Label htmlFor="country">Country *</Label>
              <Select 
                value={manualVenueData.country} 
                onValueChange={(value) => updateManualVenueData('country', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {locationService.getCountries().map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="state-province">State/Province *</Label>
              <Select 
                value={manualVenueData.state_province} 
                onValueChange={(value) => updateManualVenueData('state_province', value)}
                disabled={!manualVenueData.country}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state/province" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="city">City *</Label>
              <Select 
                value={manualVenueData.city} 
                onValueChange={(value) => updateManualVenueData('city', value)}
                disabled={!manualVenueData.state_province}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {availableCities.map((city) => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="zip-code">Zip Code *</Label>
              <Input
                id="zip-code"
                value={manualVenueData.zip_code}
                onChange={(e) => updateManualVenueData('zip_code', e.target.value)}
                placeholder="e.g., 12345"
              />
            </div>

            <div>
              <Label htmlFor="address-line">Address Line (Optional)</Label>
              <Input
                id="address-line"
                value={manualVenueData.address_line}
                onChange={(e) => updateManualVenueData('address_line', e.target.value)}
                placeholder="e.g., 123 Main Street"
              />
            </div>

            <Button 
              onClick={handleManualVenueSubmit}
              className="w-full"
              disabled={!manualVenueData.venue_name || !manualVenueData.country || 
                       !manualVenueData.state_province || !manualVenueData.city}
            >
              Submit Venue Information
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};